import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, Avatar } from '../ui'
import { getNFLDraft } from '../api'

// Draft board — VALUE OVER REPLACEMENT for YOUR league. The number of teams (and
// starting slots) sets how deep each position gets drafted, which is what makes a
// position scarce. Every player carries a target ROUND (from the value rank) and a
// tier "cliff" flag, so you can see when to take someone and what drops off after.

const POSES = ['ALL', 'QB', 'RB', 'WR', 'TE']
const TEAM_SIZES = [8, 10, 12, 14]
const posColor = p => p === 'QB' ? '#a78bfa' : p === 'RB' ? T.green : p === 'WR' ? T.mint : T.amber
const roundColor = r => r <= 2 ? T.green : r <= 5 ? T.mint : r <= 9 ? T.amber : T.muted2
const ls = (k, d) => { try { const v = localStorage.getItem(k); return v == null ? d : JSON.parse(v) } catch (_) { return d } }
const save = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)) } catch (_) {} }

function Chip({ on, onClick, children, color }) {
  return (
    <button onClick={onClick} style={{ flexShrink: 0, padding: '6px 13px', borderRadius: 20, cursor: 'pointer',
      fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', border: `1px solid ${on ? (color || T.green) : T.border}`,
      background: on ? (color || T.green) : T.card, color: on ? '#04140D' : T.muted }}>{children}</button>
  )
}

export default function DraftPanel({ season, scoring, onPlayer }) {
  const [teams, setTeams] = useState(() => ls('brkr_nfl_draft_teams', 12))
  const [superflex, setSuperflex] = useState(() => ls('brkr_nfl_draft_sf', 0))
  const [data, setData] = useState(null)
  const [pos, setPos] = useState('ALL')
  const [q, setQ] = useState('')

  useEffect(() => { save('brkr_nfl_draft_teams', teams) }, [teams])
  useEffect(() => { save('brkr_nfl_draft_sf', superflex) }, [superflex])

  useEffect(() => {
    let alive = true; setData(null)
    getNFLDraft(season, { teams, scoring, superflex }).then(d => { if (alive) setData(d) })
    return () => { alive = false }
  }, [season, teams, scoring, superflex])

  const rows = data?.rows || []
  const ql = q.trim().toLowerCase()
  const shown = rows.filter(r =>
    (pos === 'ALL' || r.pos === pos) && (!ql || r.name.toLowerCase().includes(ql)))

  return (
    <div>
      {/* What this is */}
      <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.5, marginBottom: 10 }}>
        Ranked by projected <b style={{ color: T.text }}>season points over replacement</b> for your league.
        More teams ⇒ positions get drafted deeper ⇒ studs rise. <b style={{ color: T.text }}>Rd</b> = the round a
        player’s value fits; <span style={{ color: T.amber }}>⚠ = last of his tier</span>, draft before the drop.
      </div>

      {/* League size */}
      <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 800, letterSpacing: '0.04em', marginBottom: 5 }}>LEAGUE SIZE</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {TEAM_SIZES.map(n => <Chip key={n} on={teams === n} onClick={() => setTeams(n)}>{n} teams</Chip>)}
        <Chip on={!!superflex} onClick={() => setSuperflex(superflex ? 0 : 1)} color={T.mint}>
          {superflex ? '✓ ' : ''}Superflex</Chip>
      </div>

      {/* Position filter */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 10, scrollbarWidth: 'none' }}>
        {POSES.map(p => <Chip key={p} on={pos === p} onClick={() => setPos(p)}
          color={p === 'ALL' ? T.green : posColor(p)}>{p}</Chip>)}
      </div>

      <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search players…"
        style={{ width: '100%', boxSizing: 'border-box', background: T.card2, border: `1px solid ${T.border}`,
          borderRadius: 10, padding: '10px 12px', color: T.text, fontSize: 13, marginBottom: 10, outline: 'none' }} />

      {/* Thin-pool warning — a partial season-projection load inflates replacement and
          floats scarce positions (TE) up the board. Surface it instead of mis-ranking. */}
      {data?.coverage && data.coverage.ok === false && (
        <div style={{ background: `${T.amber}18`, border: `1px solid ${T.amber}66`, borderRadius: 10,
          padding: '9px 12px', marginBottom: 10, fontSize: 11.5, color: T.text, lineHeight: 1.5 }}>
          <b style={{ color: T.amber }}>⚠ Partial board.</b> Thin projection pool at{' '}
          <b>{(data.coverage.thin_positions || []).join(', ')}</b> — ranks are approximate until the
          full rosters + season projections reload. Pool: {Object.entries(data.coverage.pool || {})
            .map(([p, n]) => `${p} ${n}`).join(' · ')}.
        </div>
      )}

      {!data ? <Spinner />
        : !rows.length ? <Empty message="No season projections yet — run the full pipeline (Ops) to build the board." />
        : !shown.length ? <Empty message={ql ? `No players match “${q}”.` : 'No players at this position.'} />
        : shown.map(r => {
          const open = () => onPlayer && onPlayer({ gsis_id: r.gsis_id, full_name: r.name, pos: r.pos,
            team: r.team, headshot: r.headshot, note: `Draft · ${r.points} proj pts · ${r.pos}${r.pos_rank} · Rd ${r.round}`, status: 'active' })
          return (
            <div key={r.gsis_id} onClick={open} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
              background: T.card, border: `1px solid ${r.cliff ? `${T.amber}66` : T.border}`, borderRadius: 11, marginBottom: 6, cursor: 'pointer' }}>
              <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 13, color: T.muted, width: 22, textAlign: 'right', flexShrink: 0 }}>{r.rank}</span>
              <Avatar url={r.headshot} name={r.name} team={r.team} size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                <div style={{ fontSize: 10.5, color: T.muted, marginTop: 1 }}>
                  <span style={{ color: posColor(r.pos), fontWeight: 800 }}>{r.pos}{r.pos_rank}</span> · {r.team}
                  <span style={{ color: T.faint }}> · T{r.tier}</span>
                  {r.cliff && <span style={{ color: T.amber, fontWeight: 700 }}> · ⚠ last of tier</span>}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 13, color: roundColor(r.round),
                  background: T.card2, border: `1px solid ${roundColor(r.round)}55`, borderRadius: 7, padding: '2px 8px' }}>Rd {r.round}</div>
                <div style={{ fontSize: 10, color: T.muted2, marginTop: 3, fontFamily: T.mono }}>{r.points} pts · +{r.vor}</div>
              </div>
            </div>
          )
        })}

      {data && rows.length > 0 && (
        <div style={{ fontSize: 10, color: T.faint, marginTop: 8, lineHeight: 1.5 }}>
          {rows.length} players ranked · {teams}-team {scoring.toUpperCase()}{superflex ? ' superflex' : ''} ·
          replacement level: {Object.entries(data.replacement || {}).map(([p, v]) => `${p} ${v}`).join(' · ')}
        </div>
      )}
    </div>
  )
}
